import './print-trim.menu.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TrimUiManager } from '../../../../../trim-editor/manager/ui/trim.ui.manager';
import { Button } from '../../../../ui/button/button';
import { Checkbox } from '../../../../ui/input/checkbox';
import { ScreenColorType } from '../../../layout/menu/layout.menu';
import { UiManager } from '../../../../../layout-editor/manager/ui/ui.manager';
import { ModeInOutType, PrintTrimMenuType } from '../hooks/use-print-trim';
import { push } from 'connected-react-router';
import { PathParams, RoutingPath } from '../../../../../routes/routing-path';
import { useParams } from 'react-router-dom';
import { PrintItemDataEntity } from '../../../../../models/sift/json/print-model';
import { useDispatch } from 'react-redux';
import { dialogActions } from '../../../../dialog/slice/dialog-slice';
import { useQuery } from '../../../../../hooks/use-query';

type layoutModeType = {
  mode: ModeInOutType,
  label: string,
}

const layoutModeList: layoutModeType[] = [
  { mode: 'out', label: '外接' },
  { mode: 'in', label: '内接' },
  { mode: 'eq', label: 'ピクセル等倍' },
];

type Props = {
  initMenu: PrintTrimMenuType,
  item: PrintItemDataEntity | null,
}

const PrintTrimMenu = (props: Props) => {
  const { initMenu, item } = props;
  const { kijshopCd, shopOrderId } = useParams<PathParams>();
  const query = useQuery();
  const dispatch = useDispatch();
  // - State -
  // -- 余白の許可 --
  const [isAllowMargin, setIsAllowMargin] = useState(initMenu.isAllowMargin);
  // -- 背景色 --
  const [screenColor, setScreenColor] = useState<ScreenColorType>(initMenu.screenColor);
  // -- 有効保障エリア --
  const [isShowArea, setIsShowArea] = useState(initMenu.modeAbleArea === 'on');
  // -- 内接or外接orピクセル等倍ステート --
  const [layoutMode, setLayoutMode] = useState<ModeInOutType>(initMenu.modeInOut);
  // -- メインメニューの種類 --
  const [menuType, setMenuType] = useState<'album' | 'request' | 'delete' | 'change' | ''>('');
  // -- UIポップの表示位置 --
  const [popPosY, setPopPosY] = useState(0);
  // - Ref -
  const ref = useRef<HTMLDivElement>(null);

  // - callback -
  // -- 有効保障エリアON --
  const handlerClickChangeScreenColor = useCallback(() => {
    const nextColor = (() => {
      switch (screenColor) {
        case 'black':
          return 'gray';
        case 'gray':
          return 'white';
        case 'white':
          return 'black';
      }
    })();
    setScreenColor(nextColor);
    TrimUiManager.ins.emit('r->t:change-screen-color', {
      ...initMenu,
      modeAbleArea: isShowArea ? 'on' : 'off',
      isAllowMargin,
      modeInOut: layoutMode,
      screenColor: nextColor
    });
  }, [initMenu, screenColor, isShowArea, isAllowMargin, layoutMode])
  // -- 余白を許可する --
  const handlerClickAllowMargin = useCallback((isAllowMargin: boolean) => {
    setIsAllowMargin(isAllowMargin);
    TrimUiManager.ins.emit('r->t:change-allow-margin', { isAllowMargin });
  }, []);
  // -- 有効保障エリアON --
  const handlerClickValidSecurityArea = useCallback((isSecurityArea: boolean) => {
    setIsShowArea(isSecurityArea);
    TrimUiManager.ins.emit('r->t:image-guide-on/off', { mode: isSecurityArea ? 'on' : 'off' });
  }, []);
  // -- 内接 --
  const handlerClickLayoutMode = useCallback((mode: layoutModeType['mode']) => {
    if (!isAllowMargin && (mode === 'in' || mode === 'eq')) {
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: [
          `「${mode === 'in' ? '内接' : 'ピクセル等倍'}」は`,
          '「余白を許可する」がチェック有りの場合のみ実行可能です'
        ],
        buttons: [{ label: 'OK', callback: () => dispatch(dialogActions.pop()) }]
      }))
      return;
    }
    setLayoutMode(mode);
    TrimUiManager.ins.emit('r->t:image-resize-in/out/eq', { mode });
  }, [isAllowMargin]);

  // -- 内接・外接・ピクセル等倍のボタンカラー --
  const getButtonColor = (mode: string) => (layoutMode === mode ? 'primary' : 'dark');

  // -- 戻る --
  const handlerClickBack = useCallback(() => {
    if (!item) {
      throw new Error('item not found !!!!!!!!!!!!!!');
    }
    dispatch(dialogActions.pushMessage({
      title: '確認',
      message: [
        '保存していない内容は破棄されます。よろしいですか？'
      ],
      buttons: [
        {
          label: 'いいえ',
          callback: () => {
            dispatch(dialogActions.pop());
          },
        },
        {
          label: 'はい',
          callback: () => {
            const modeQuery = query.get('k') ? `&k=${query.get('k')}` : '';
            dispatch(dialogActions.pop());
            dispatch(push(`${RoutingPath.cnv.printOrderSelectImage({ kijshopCd, shopOrderId })}?t=${item.id}${modeQuery}`));
          },
        },
      ]
    }))
  }, [
    item,
    query,
  ]);

  useEffect(() => {
    setIsAllowMargin(initMenu.isAllowMargin);
    setScreenColor(initMenu.screenColor);
    setIsShowArea(initMenu.modeAbleArea === 'on');
    setLayoutMode(initMenu.modeInOut);
  }, [initMenu]);

  useEffect(() => {
    TrimUiManager.ins.on('t->r:image-resize-in/out/eq', (v) => {
      setLayoutMode(v.mode);
    });
    TrimUiManager.ins.on('t->r:change-allow-margin', (v) => {
      setIsAllowMargin(v.isAllowMargin);
    });
    return () => {
      TrimUiManager.ins.off('t->r:image-resize-in/out/eq');
      TrimUiManager.ins.off('t->r:change-allow-margin');
    }
  }, [])

  return (
    <div
      className="trim_menu"
      ref={ref}
      onClick={() => TrimUiManager.ins.emit('r->t:dis-select')}
    >
      <div className="main_menu"></div>
      <div>
        <div className="allow_margin_switch">
          <span>余白を許可する</span>
          <Checkbox
            checked={isAllowMargin}
            onClick={() => handlerClickAllowMargin(!isAllowMargin)}
          />
        </div>
        <div className="screen_color_switch">
          <span>背景色</span>
          <div>
            <Button
              label="変更"
              onClick={handlerClickChangeScreenColor}
              color="primary"
              size="sm"
              tooltip={{ type: 'normal', text: ['クリックすると黒→グレー→白の順で', '背景色が切り替わります。'] }}
            />
          </div>
        </div>
        <div className="guarantee_area_switch">
          <span>有効保障エリア</span>
          <div>
            <Button
              label="ON"
              onClick={() => handlerClickValidSecurityArea(true)}
              color={isShowArea ? 'primary' : 'dark'}
              size="sm"
            />
            <Button
              label="OFF"
              onClick={() => handlerClickValidSecurityArea(false)}
              color={!isShowArea ? 'primary' : 'dark'}
              size="sm"
            />
          </div>
        </div>

        <div className="trim_switch">
          <span>レイアウト</span>
          <div>
            {layoutModeList.map(({ mode, label }) => (
              <Button
                key={mode}
                label={label}
                onClick={() => handlerClickLayoutMode(mode)}
                color={getButtonColor(mode)}
                size="sm"
                tooltip={{
                  type: 'normal',
                  text: ['クリックすると次のレイアウト時から', `${label}でレイアウトされるように変更します。`],
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <div>
        <Button
          label="戻る"
          onClick={handlerClickBack}
          color="danger"
          className="btn_back"
          size="sm"
        />
      </div>
    </div>
  );
};

export default PrintTrimMenu;
